const ACTIVE_STATUSES = new Set(['generating', 'queued', 'pending', 'running', 'processing', 'created']);
const FAILED_STATUSES = new Set(['failed', 'error', 'cancelled', 'canceled']);
const DOWNLOAD_FORMATS = new Set(['docx', 'pptx']);

function token(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function objectOrEmpty(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function firstPresent(...values) {
  return values.find(v => v !== undefined && v !== null && v !== '');
}

function firstBoolean(...values) {
  return values.find(v => typeof v === 'boolean');
}

function firstArray(...values) {
  return values.find(Array.isArray);
}

function normalizeFormats(values, fallback = []) {
  const raw = Array.isArray(values) ? values : fallback;
  return raw
    .map(format => format?.toString().toLowerCase())
    .filter(format => DOWNLOAD_FORMATS.has(format));
}

function formatFromContentType(value) {
  const contentType = token(value);
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'pptx';
  if (contentType.includes('wordprocessing') || contentType.includes('document')) return 'docx';
  return '';
}

export function reportArtifactState(report = {}, fallbackFormats = []) {
  const artifact = objectOrEmpty(report?.artifact);
  const explicitReady = firstBoolean(report?.artifactReady, artifact.ready, artifact.artifactReady, artifact.hasFile);
  const blobPath = firstPresent(report?.blobPath, artifact.blobPath, artifact.path, artifact.storagePath);
  const hasArtifactLocation = !!firstPresent(blobPath, artifact.url, artifact.downloadUrl);
  const legacyHasFile = report?.hasFile === true || hasArtifactLocation;
  const hasFile = explicitReady ?? legacyHasFile;
  const readinessStatus = explicitReady === false ? 'waiting_for_artifact' : explicitReady === true ? 'ready' : undefined;
  const status = firstPresent(report?.artifactStatus, artifact.artifactStatus, artifact.status, readinessStatus, report?.status);
  const rawFormats = firstArray(report?.availableFormats, artifact.availableFormats, artifact.formats);
  const singleFormat = firstPresent(report?.format, artifact.format, formatFromContentType(artifact.contentType));
  const availableFormats = normalizeFormats(rawFormats ?? (singleFormat ? [singleFormat] : null), fallbackFormats);

  return {
    artifact,
    artifactStatus: status ?? null,
    hasFile,
    availableFormats,
    blobPath: blobPath ?? null,
  };
}

export function normalizeReportStatus(value, hasFile = false, artifactStatus = null) {
  const status = token(firstPresent(artifactStatus, value));
  if (FAILED_STATUSES.has(status)) return 'failed';
  if (ACTIVE_STATUSES.has(status)) return 'generating';
  if (status === 'ready' && !hasFile) return 'waiting_for_artifact';
  if (status === 'complete' || status === 'completed' || status === 'success') return 'ready';
  return status || (hasFile ? 'ready' : 'waiting_for_artifact');
}

function errorText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.message || value.error || value.detail || value.title || '';
  }
  return String(value);
}

export function reportFailureMessage(report) {
  const artifact = objectOrEmpty(report?.artifact);
  return errorText(report?.error)
    || errorText(artifact.error)
    || report?.errorMessage
    || report?.lastErrorMessage
    || report?.failureReason
    || report?.statusMessage
    || '';
}

export function reportNeedsPolling(report) {
  const artifact = reportArtifactState(report);
  const status = normalizeReportStatus(report?.status, artifact.hasFile, artifact.artifactStatus);
  return status === 'generating';
}

export function reportStatusPresentation(report) {
  const artifact = reportArtifactState(report, report?.availableFormats ?? []);
  const status = normalizeReportStatus(report?.status, artifact.hasFile, artifact.artifactStatus);
  if (status === 'ready') {
    return {
      label: 'Ready',
      tone: 'success',
      detail: artifact.availableFormats.length > 0 ? 'Artifact ready for download.' : 'Report record is ready.',
    };
  }
  if (status === 'generating') {
    return { label: 'Generating', tone: 'warning', detail: 'Report job is still running.' };
  }
  if (status === 'failed') {
    return {
      label: 'Failed',
      tone: 'danger',
      detail: reportFailureMessage(report) || 'Report generation failed.',
    };
  }
  if (status === 'waiting_for_artifact') {
    return { label: 'Waiting for artifact', tone: 'warning', detail: 'Report record exists, but the DOCX/PPTX artifact is not ready yet.' };
  }
  return { label: status.replace(/_/g, ' ') || 'Unknown', tone: 'muted', detail: 'Report status is available from the API.' };
}
